"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
// TODO: Add types for linear-algebra-js.
const linear_algebra_js_1 = require("linear-algebra-js");
const common_1 = require("../common");
const core_1 = require("../core");
const analysis_1 = require("../analysis");
const EPS = 1e-6;
/**
 * A quasi-static solver for folding triangulated meshes.
 */
class Solver {
    constructor() {
        this._isInitialized = false;
        this._axialK = 100;
        this._creaseK = 1;
        this._faceK = 10;
        this._listeners = [];
        this._triangleMesh = {
            vertices: [],
            edges: [],
            faces: [],
            verticesForwardMapping: [],
            edgesForwardMapping: [],
            facesForwardMapping: [],
        };
        this._groups = [];
        this._forwardGroupLookup = [];
        this._crease = new core_1.Crease([], [], []);
        this._positions = [];
        this._normals = [];
        this._nominalEdgeLengths = [];
        this._targetDihedrals = [];
        this._dihedrals = [];
        this._angularVelocities = [];
        this._verticesMapping = [];
        this._edgesMapping = [];
        this._creasesMapping = [];
        // Clear all stored data.
        this._clear();
    }
    /**
     * Indicates whether this solver has been initialized.
     * @returns {boolean} True if initialized; false otherwise.
     */
    get isInitialized() {
        return this._isInitialized;
    }
    /**
     * Initialization function for Solver class.
     * A Solver object may be re-initialized with new parameters and reused.
     * @param {Crease} crease A crease object.
     * @param {TriangleMesh} triangleMesh A triangle mesh with vertices, edges, faces,
     * and facesForwardMapping fields populated.
     * @param {number} [axialK = 100] The axial stiffness of the edge constraints.
     * @param {number} [creaseK = 1] The angular stiffness of the crease constraints.
     * @param {number} [faceK = 10] The angular stiffness of the face planarity constraints.
     * @param {number} [fixedFace = 0] A single fixed face index  (corresponding to a CreaseFace).
     */
    initialize(crease, triangleMesh, axialK = 100, creaseK = 1, faceK = 10, fixedFace = 0) {
        // Check input.
        if (!triangleMesh) {
            throw new Error('No triangle mesh passed into Solver.initialize().');
        }
        common_1.check.hasProperty('triangleMesh', triangleMesh, 'vertices');
        common_1.check.hasProperty('triangleMesh', triangleMesh, 'edges');
        common_1.check.hasProperty('triangleMesh', triangleMesh, 'faces');
        common_1.check.hasProperty('triangleMesh', triangleMesh, 'edgesForwardMapping');
        common_1.check.hasProperty('triangleMesh', triangleMesh, 'facesForwardMapping');
        // Each entry of triangleMesh.vertices has properties:
        // {
        //     position2D,
        //     parent,
        //     index,
        // }
        common_1.check.isArray('triangleMesh.vertices', triangleMesh.vertices, [3, Infinity], (vertexName, vertex) => {
            common_1.check.is2DPoint(`${vertexName}.position2D`, vertex.position2D);
            if (vertex.parent !== undefined) {
                common_1.check.isNonNegativeInteger(`${vertexName}.parent`, vertex.parent);
            }
            common_1.check.isNonNegativeInteger(`${vertexName}.index`, vertex.index);
            common_1.check.isInRange(`${vertexName}.index`, vertex.index, 0, triangleMesh.vertices.length - 1);
        });
        // Each entry of triangleMesh.edges has properties:
        // {
        //     edgeVertex1,
        //     edgeVertex2,
        //     leftFace,
        //     rightFace,
        //     leftVertex,
        //     rightVertex,
        //     targetLength,
        //     parent,
        // }
        common_1.check.isArray('triangleMesh.edges', triangleMesh.edges, [3, Infinity], (edgeName, edge) => {
            common_1.check.isNonNegativeInteger(`${edgeName}.edgeVertex1`, edge.edgeVertex1);
            common_1.check.isInRange(`${edgeName}.edgeVertex1`, edge.edgeVertex1, 0, triangleMesh.vertices.length - 1);
            common_1.check.isNonNegativeInteger(`${edgeName}.edgeVertex2`, edge.edgeVertex2);
            common_1.check.isInRange(`${edgeName}.edgeVertex2`, edge.edgeVertex2, 0, triangleMesh.vertices.length - 1);
            if (edge.leftFace !== undefined) {
                common_1.check.isNonNegativeInteger(`${edgeName}.leftFace`, edge.leftFace);
                common_1.check.isInRange(`${edgeName}.leftFace`, edge.leftFace, 0, triangleMesh.faces.length - 1);
            }
            if (edge.rightFace !== undefined) {
                common_1.check.isNonNegativeInteger(`${edgeName}.rightFace`, edge.rightFace);
                common_1.check.isInRange(`${edgeName}.rightFace`, edge.rightFace, 0, triangleMesh.faces.length - 1);
            }
            if (edge.leftVertex !== undefined) {
                common_1.check.isNonNegativeInteger(`${edgeName}.leftVertex`, edge.leftVertex);
                common_1.check.isInRange(`${edgeName}.leftVertex`, edge.leftVertex, 0, triangleMesh.vertices.length - 1);
            }
            if (edge.rightVertex !== undefined) {
                common_1.check.isNonNegativeInteger(`${edgeName}.rightVertex`, edge.rightVertex);
                common_1.check.isInRange(`${edgeName}.rightVertex`, edge.rightVertex, 0, triangleMesh.vertices.length - 1);
            }
            common_1.check.isNumber(`${edgeName}.targetLength`, edge.targetLength);
            if (edge.parent !== undefined) {
                common_1.check.isNonNegativeInteger(`${edgeName}.parent`, edge.parent);
            }
        });
        // Each entry of triangleMesh.faces has properties:
        // {
        //     vertices,
        //     parent,
        // }
        common_1.check.isArray('triangleMesh.faces', triangleMesh.faces, [1, Infinity], (faceName, face) => {
            common_1.check.isArray(`${faceName}.vertices`, face.vertices, 3, (vertexName, vertexIndex) => {
                common_1.check.isNonNegativeInteger(vertexName, vertexIndex);
                common_1.check.isInRange(vertexName, vertexIndex, 0, triangleMesh.vertices.length - 1);
            });
            common_1.check.isNonNegativeInteger(`${faceName}.parent`, face.parent);
        });
        // edgesForwardMapping is an array of edge indices.
        common_1.check.isArray('triangleMesh.edgesForwardMapping', triangleMesh.edgesForwardMapping, [1, Infinity], (name, edgeMapping) => {
            common_1.check.isArray(name, edgeMapping, [0, triangleMesh.edges.length], (edgeName, edgeIndex) => {
                common_1.check.isNonNegativeInteger(edgeName, edgeIndex);
                common_1.check.isInRange(edgeName, edgeIndex, 0, triangleMesh.edges.length - 1);
            });
        });
        // facesForwardMapping is an array of face indices.
        common_1.check.isArray('triangleMesh.facesForwardMapping', triangleMesh.facesForwardMapping, [1, Infinity], (name, faceMapping) => {
            common_1.check.isArray(name, faceMapping, [1, triangleMesh.faces.length], (faceName, faceIndex) => {
                common_1.check.isNonNegativeInteger(faceName, faceIndex);
                common_1.check.isInRange(faceName, faceIndex, 0, triangleMesh.faces.length - 1);
            });
        });
        // fixedFace is a face index.
        common_1.check.isNonNegativeInteger('fixedFace', fixedFace);
        common_1.check.isInRange('fixedFace', fixedFace, 0, crease.faces.length - 1);
        common_1.check.isNumber('axialK', axialK);
        common_1.check.isPositive('axialK', axialK);
        common_1.check.isNumber('creaseK', creaseK);
        common_1.check.isPositive('creaseK', creaseK);
        common_1.check.isNumber('faceK', faceK);
        common_1.check.isPositive('faceK', faceK);
        // Store stiffness params to "this".
        this._axialK = axialK;
        this._creaseK = creaseK;
        this._faceK = faceK;
        // Store vertices, edges, faces from triangulated mesh to "this".
        this._triangleMesh = triangleMesh;
        // Store groups and crease for rigid transforms.
        const { groups, forwardGroupLookup } = analysis_1.collapseCycles(crease);
        this._groups = groups;
        this._forwardGroupLookup = forwardGroupLookup;
        this._crease = crease;
        // Store the edge lengths in the undeformed crease pattern.
        this._nominalEdgeLengths = this._triangleMesh.edges.map(edge => edge.targetLength);
        // Set initial states.
        this.reset();
        this.setFixedFace(fixedFace);
        this._isInitialized = true;
    }
    /**
     * Fixes a face of the mesh so it does not move.
     * @param {number} [fixedFace = 0] A single fixed face index (corresponding to a CreaseFace).
    */
    setFixedFace(fixedFace) {
        // fixedFace is a face index.
        common_1.check.isInRange('fixedFace', fixedFace, 0, this._crease.faces.length - 1);
        this._M = undefined;
        this._G = undefined;
        this._displacementVector = undefined;
        // Let the Emscripten memory manager know it can free all heap-allocated matrices.
        linear_algebra_js_1.memoryManager.deleteExcept([]);
        // Compute all derived data.
        // (The order of these next few lines is somewhat sensitive.)
        // This solver updates vertex positions only,
        // so we must convert fixed faces into a set of fixed vertices.
        const fixedVertices = this._calcFixedVertices(fixedFace, this._triangleMesh.facesForwardMapping);
        // Calculate the mappings between vertices, edges, and creases and their
        // indices in the M, C, and G matrices.
        const { verticesMapping, edgesMapping, creasesMapping, } = this._calcMatrixIndexMappings(fixedVertices);
        this._verticesMapping = verticesMapping;
        this._edgesMapping = edgesMapping;
        this._creasesMapping = creasesMapping;
        // Calc subtrees for groups, relative to fixed face.
        this._groups = analysis_1.computeSubtreeFaces(fixedFace, this._groups);
        // Save the last dihedral angles of the active creases, this is used to
        // maintain continuity across 2pi <-> 0 boundary as we calculate new dihedral angles.
        // this._dihedrals is updated each time step() is called.
        this._dihedrals = this._calcDihedrals();
        this._angularVelocities = this._triangleMesh.edges.map(() => undefined);
        // Initialize M and G matrices.
        this._M = this._calcMassMatrix();
        this._G = this._calcGMatrix();
    }
    /**
     * Sets the target dihedral angles for the triangulated mesh, given an array of fold angles
     * corresponding to creases in a parent Crease object.
     * @param {(number | null)[]} targetCreaseAngles Array of fold angles (degrees) corresponding
     * to creases in a parent Crease object.
     */
    setTargetCreaseAngles(targetCreaseAngles) {
        // given crease angles, convert to target dihedral of triangleMesh
        const { _triangleMesh } = this;
        this._targetDihedrals = _triangleMesh.edges.map((edge) => {
            if (edge.parent === undefined) { // Facet crease.
                return 0;
            }
            const targetCreaseAngle = targetCreaseAngles[edge.parent];
            if (targetCreaseAngle === null) {
                return undefined;
            }
            return common_1.utils.convertToRadians(targetCreaseAngle);
        });
    }
    /**
     * Sets one target dihedral angles for the triangulated mesh.
     * @param {number} targetCreaseAngle angle (degrees)
     * @param {number} edgeIndex index of edge in Crease object
     */
    setTargetCreaseAngleForEdgeIndex(targetCreaseAngle, edgeIndex) {
        // given crease angles, convert to target dihedral of triangleMesh
        const { _triangleMesh, _targetDihedrals } = this;
        const segmentIndices = _triangleMesh.edgesForwardMapping[edgeIndex];
        const angleInRadians = common_1.utils.convertToRadians(targetCreaseAngle);
        segmentIndices.forEach((index) => {
            if (_targetDihedrals[index] !== angleInRadians) {
                _targetDihedrals[index] = angleInRadians;
            }
        });
    }
    /**
     * Returns the current positions of vertices in the solver.
     * @returns {Vector3[]} Array of 3D positions, one for each vertex in mesh.
     */
    get positions() {
        return this._positions.slice();
    }
    /**
     * Returns the flat positions of vertices in the solver.
     * @returns {Vector3[]} Array of 3D positions, one for each vertex in mesh.
     */
    get flatPositions() {
        return this._triangleMesh.vertices.map(vertex => [...vertex.position2D, 0]);
    }
    /**
     * Sets the current positions of vertices in the solver.
     * This is used when rotating a folded mesh so that a particular face is "down".
     * @param {Vector3} positions The new positions of the vertices.
     */
    set positions(positions) {
        this._positions = positions;
        this._normals = this._calcNormals();
        this._dihedrals = this._calcDihedrals();
        this._angularVelocities = this._triangleMesh.edges.map(() => 0);
        // Notify all listeners.
        this._listeners.forEach((listener) => {
            listener(this.positions, this.dihedrals, this.normals, this.triangleMesh);
        });
    }
    get maxDisplacement() {
        const { _displacementVector, _verticesMapping } = this;
        if (_displacementVector === undefined) {
            return 0;
        }
        let maxDispSq = 0;
        _verticesMapping.forEach((map) => {
            if (map === undefined) {
                return;
            }
            const disp = [
                _displacementVector.get(3 * map, 0),
                _displacementVector.get(3 * map + 1, 0),
                _displacementVector.get(3 * map + 2, 0),
            ];
            const dispSq = common_1.vec.lengthSquared(disp);
            if (dispSq > maxDispSq) {
                maxDispSq = dispSq;
            }
        });
        return Math.sqrt(maxDispSq);
    }
    /**
     * Returns the current normals of traingles in the solver.
     * @returns {Vector3[]} Array of normals, one for each triangular face in mesh.
     */
    get normals() {
        return this._normals.slice();
    }
    /**
     * Returns the flat normals of traingles in the solver.
     * @returns {Vector3[]} Array of normals, one for each triangular face in mesh.
     */
    get flatNormals() {
        return this._normals.map(() => [0, 0, 1]);
    }
    /**
     * Returns the last computed dihedrals of edges in the solver.
     * @returns {OptionalNumberArray} Array of dihedrals, one for each edge in mesh.
     */
    get dihedrals() {
        return this._dihedrals.slice();
    }
    /**
     * Returns the flat dihedrals of edges in the solver.
     * @returns {OptionalNumberArray} Array of dihedrals, one for each edge in mesh.
     */
    get flatDihedrals() {
        return this._dihedrals.map(() => 0);
    }
    /**
     * Returns the angular velocity of edges in the solver.
     * @returns {OptionalNumberArray} Array of anglular velocities, one for each edge in mesh.
     */
    get angularVelocities() {
        return this._angularVelocities.slice();
    }
    /**
     * Returns the last target dihedrals of edges in the solver.
     * @returns {OptionalNumberArray} Array of dihedrals, one for each edge in mesh.
     */
    get targetDihedrals() {
        return this._targetDihedrals.slice();
    }
    /**
     * Returns the current triangleMesh in the solver.
     * @returns {TriangleMesh} TriangleMesh object.
     */
    get triangleMesh() {
        return this._triangleMesh;
    }
    /**
     * Calculates the fixed vertices of the triangleMesh from fixed faces.
     * @private
     * @param {number[]} fixedFaces An array of fixed face indices from
     * the crease object that spawned the triangle mesh.
     * @param {FacesForwardMapping} facesForwardMapping An array mapping crease face
     * indices to triangle indices in triangleMesh.
     * @returns {boolean[]} An array corresponding to this.vertices with true
     * for each fixed vertex and false for each free vertex.
     */
    _calcFixedVertices(fixedFace, facesForwardMapping) {
        const { _triangleMesh } = this;
        // All vertices attached to a fixed face are marked fixed.
        const fixedTriangles = facesForwardMapping[fixedFace];
        return _triangleMesh.vertices.map((vertex, index) => fixedTriangles
            .some(tri => _triangleMesh.faces[tri].vertices.indexOf(index) >= 0));
    }
    /**
     * Calculate the mappings that between vertices, edges, and creases and their
     * indices in the M, C, and G matrices.
     * @private
     * @param {boolean[]} fixedVertices An array corresponding to this.vertices with true
     * for each fixed vertex and false for each free vertex.
     * @returns {IndexMappings} An object with verticesMapping, edgeMapping, and creasesMapping.
     */
    _calcMatrixIndexMappings(fixedVertices) {
        const { _triangleMesh } = this;
        // Mapping of vertex indices to M and C matrices.
        const verticesMapping = [];
        let i = 0;
        fixedVertices.forEach((state) => {
            if (!state) {
                verticesMapping.push(i);
                i += 1;
            }
            else {
                verticesMapping.push(undefined);
            }
        });
        // Mapping of edge indices to C and G matrices.
        const edgesMapping = [];
        i = 0;
        _triangleMesh.edges.forEach((edge) => {
            if (!fixedVertices[edge.edgeVertex1] || !fixedVertices[edge.edgeVertex2]) {
                edgesMapping.push(i);
                i += 1;
            }
            else {
                // Ignore fixed vertices.
                edgesMapping.push(undefined);
            }
        });
        // Mapping of crease indices to C and G matrices.
        // Index i continues from edgesMapping.
        const creasesMapping = [];
        _triangleMesh.edges.forEach((edge) => {
            if (edge.leftFace !== undefined && edge.rightFace !== undefined) {
                creasesMapping.push(i);
                i += 1;
            }
            else {
                // Ignore boundary edges.
                // TODO: identify fixed creases (both adjacent faces are fixed)
                creasesMapping.push(undefined);
            }
        });
        return {
            verticesMapping,
            edgesMapping,
            creasesMapping,
        };
    }
    /**
     * Returns the number of defined elements in an array.
     * @private
     * @param {Array} array The array to check.
     * @returns {number} The number on defined elements in an array.
    */
    /* eslint-disable-next-line class-methods-use-this */
    _countDefined(array) {
        return common_1.utils.countIf(array, item => item !== undefined);
    }
    /**
     * Returns an array of axial stiffnesses for each edge in mesh.
     * @private
     * @returns {number[]} Array of stiffnesses, corresponding to each edge in the mesh.
     */
    _calcEdgesK() {
        const { _triangleMesh, _axialK } = this;
        // Geometric edge stiffness is scaled by 1 / length.
        return _triangleMesh.edges.map(edge => _axialK / edge.targetLength);
    }
    /**
     * Returns an array of angular stiffnesses for each crease in mesh.
     * @private
     * @returns {number[]} Array of stiffnesses, corresponding to each crease in the mesh.
     */
    _calcCreasesK() {
        const { _triangleMesh, _creaseK, _faceK } = this;
        return _triangleMesh.edges.map((edge) => {
            // Stiffness depends on crease type.
            const stiffness = edge.parent === undefined ? _faceK : _creaseK;
            // Geometric crease stiffness is scaled by length.
            return edge.targetLength * stiffness;
        });
    }
    /**
     * Returns the current length of a given edge.
     * @private
     * @param {TriangleEdge} edge The edge to measure.
     * @returns {number} The length of the edge.
     */
    _calcEdgeLength(edge) {
        const { _positions } = this;
        return common_1.vec.distance(_positions[edge.edgeVertex1], _positions[edge.edgeVertex2]);
    }
    /**
     * Returns the area of a given triangular face.
     * @private
     * @param {IndexTriplet} indices The indices of the face's vertices.
     * @returns {number} The area of the face.
     */
    _calcFaceArea(indices) {
        const { _triangleMesh } = this;
        const vec1 = common_1.vec.subtract(_triangleMesh.vertices[indices[1]].position2D, _triangleMesh.vertices[indices[0]].position2D);
        const vec2 = common_1.vec.subtract(_triangleMesh.vertices[indices[2]].position2D, _triangleMesh.vertices[indices[0]].position2D);
        return common_1.vec.wedge(vec1, vec2) / 2;
    }
    /**
     * Returns the normal of a triangular face.
     * @private
     * @param {IndexTriplet} indices Vertex indices of triangular face.
     * @returns {Vector3} Face normal as 3D vector.
     */
    _calcFaceNormal(indices) {
        const { _positions } = this;
        const vec1 = common_1.vec.subtract(_positions[indices[1]], _positions[indices[0]]);
        const vec2 = common_1.vec.subtract(_positions[indices[2]], _positions[indices[0]]);
        return common_1.vec.normalize(common_1.vec.cross(vec1, vec2));
    }
    /**
     * Returns an array of face normals for each face in the mesh.
     * @private
     * @returns {Vector3[]} Array of face normals as 3D vectors.
     */
    _calcNormals() {
        return this._triangleMesh.faces.map(face => this._calcFaceNormal(face.vertices));
    }
    /**
     * Returns the dihedral angle of a given crease.
     * @private
     * @param {TriangleEdge} edge A crease to measure.
     * @param {number} [lastTheta] The last measured dihedral angle of the crease
     * (used to test continuity across -Pi <-> Pi boundary).
     * @returns {number} The dihedral angle of the crease.
     */
    _calcEdgeDihedral(edge, lastTheta) {
        const { _positions, _normals } = this;
        const { edgeVertex1, edgeVertex2, leftFace, rightFace, } = edge;
        // Boundary edges return undefined.
        if (leftFace === undefined || rightFace === undefined) {
            return undefined;
        }
        // Calculate dihedral angle in range [-Pi, Pi]
        // https://math.stackexchange.com/questions/47059/how-do-i-calculate-a-dihedral-angle-given-cartesian-coordinates
        const creaseVector = common_1.vec.normalize(common_1.vec.subtract(_positions[edgeVertex2], _positions[edgeVertex1]));
        const n1 = _normals[leftFace];
        const n2 = _normals[rightFace];
        const x = common_1.utils.clamp(common_1.vec.dot(n1, n2), -1, 1);
        const y = common_1.utils.clamp(common_1.vec.dot(common_1.vec.cross(n1, creaseVector), n2), -1, 1);
        let theta = Math.atan2(y, x);
        // Check discontinuity at -Pi <-> PI boundary using lastTheta
        if (lastTheta === undefined) {
            return theta;
        }
        if (Number.isNaN(lastTheta)) {
            throw new Error('Invalid lastTheta supplied to calcDihedralAngle.');
        }
        let diff = theta - lastTheta;
        if (diff < -5) {
            diff += Math.PI * 2;
        }
        else if (diff > 5) {
            diff -= Math.PI * 2;
        }
        theta = lastTheta + diff;
        return theta;
    }
    /**
     * Returns an array of the dihedral angles of all the edges in the mesh.
     * Dihedral angle of boundary edges are set to undefined.
     * @private
     * @returns {OptionalNumberArray} An array of dihedral angles (or undefined for boundary edges).
     */
    _calcDihedrals() {
        const { _triangleMesh, _dihedrals } = this;
        const dihedrals = _triangleMesh.edges.map((edge, i) => this._calcEdgeDihedral(edge, _dihedrals && _dihedrals[i]));
        return dihedrals;
    }
    /**
     * Returns the jacobian of an axial constraint.  This relates the change in length
     * of the edge to the change in position of the edge's endpoints in 3 space.
     * @private
     * @param {TriangleEdge} edge The edge defining the axial constraint.
     * @returns {[Vector3, Vector3]} A 3x2 matrix giving the jacobian of the axial constraint.
     */
    _jacobCoefAxial(edge) {
        const { _positions } = this;
        const edgeVector = common_1.vec.normalize(common_1.vec.subtract(_positions[edge.edgeVertex2], _positions[edge.edgeVertex1]));
        // Get a normalized vector from v1 to v2 along the edge and scale by -1, 1.
        return [common_1.vec.multiply(edgeVector, -1), edgeVector];
    }
    /**
     * Returns the jacobian of a crease constraint.  This relates the change in dihedral angle
     * to the change in position of the edge's endpoints as well as the two vertices
     * opposite the edge in 3 space.
     * @private
     * @param {TriangleEdge} edge The edge defining the crease constraint.
     * @returns {[Vector3, Vector3, Vector3, Vector3]} A 3x4 matrix giving the jacobian of the
     * crease constraint.
     */
    _jacobCoefCrease(edge) {
        const { _positions, _normals } = this;
        const { edgeVertex1, edgeVertex2, leftVertex, rightVertex, leftFace, rightFace, } = edge;
        // Get vertex positions.
        const p1 = _positions[edgeVertex1];
        const p2 = _positions[edgeVertex2];
        const pL = _positions[leftVertex];
        const pR = _positions[rightVertex];
        // Get vectors between vertices.
        const v12 = common_1.vec.subtract(p2, p1);
        const v1L = common_1.vec.subtract(pL, p1);
        const v1R = common_1.vec.subtract(pR, p1);
        const vL2 = common_1.vec.subtract(p2, pL);
        const vR2 = common_1.vec.subtract(p2, pR);
        // Get the cotangents of the angles at p1 and p2.
        const cotL12 = common_1.vec.cotangent(v1L, v12);
        const cotL21 = common_1.vec.cotangent(vL2, v12);
        const cotR12 = common_1.vec.cotangent(v1R, v12);
        const cotR21 = common_1.vec.cotangent(vR2, v12);
        const sumL = cotL12 + cotL21;
        const sumR = cotR12 + cotR21;
        // Get the distance of the left vertex and the right vertex to the line between 1 and 2.
        // To find the distance of a point q to a line through two other points p1 and p2, consider
        // the triangle (p1, p2, q). If p1 and p2 form the base, the altitude is the distance to q.
        // Since the triangle area is the length of the base times half the altitude, the altitude
        // must be twice the area divided by the length of the base, which is the magnitude of the
        // cross product of two edges divided by the distance between p1 and p2.
        // See http://mathworld.wolfram.com/Point-LineDistance3-Dimensional.html.
        const lenV12 = common_1.vec.length(v12);
        const dL = common_1.vec.length(common_1.vec.cross(v1L, vL2)) / lenV12;
        const dR = common_1.vec.length(common_1.vec.cross(v1R, vR2)) / lenV12;
        // Get face normals and scale by distances.
        const nL = common_1.vec.divide(_normals[leftFace], dL);
        const nR = common_1.vec.divide(_normals[rightFace], dR);
        return [
            nL,
            nR,
            common_1.vec.add(common_1.vec.multiply(-cotL21 / sumL, nL), common_1.vec.multiply(-cotR21 / sumR, nR)),
            common_1.vec.add(common_1.vec.multiply(-cotL12 / sumL, nL), common_1.vec.multiply(-cotR12 / sumR, nR)),
        ];
    }
    /**
     * Returns the mass matrix M of the mesh.
     * @private
     * @returns {SparseMatrix} A matrix of dimension [numVertices * 3, numVertices * 3]
     * (excluding fixed vertices) with the mass of each vertex of the mesh along its diagonal.
     */
    _calcMassMatrix() {
        // M is a diagonal matrix of vertex masses.  Assuming constant material density,
        // divide the mass in each triangular face amongst the neighboring vertices.
        // Dimensions: [numVertices * 3, numVertices * 3] (excluding fixed vertices)
        const { _triangleMesh, _verticesMapping } = this;
        const numVertices = this._countDefined(_verticesMapping);
        // Dividing around the incenter of a triangle is same as splitting
        // the triangle into 3 equal areas.
        // http://jwilson.coe.uga.edu/emt668/EMAT6680.2000/Lehman/emat6690/trisecttri's/trisect.html
        const epsilon = 1e-8;
        // First init all vertices' mass as 0.
        const mass = new Array(numVertices).fill(0);
        _triangleMesh.faces.forEach((face) => {
            const area = this._calcFaceArea(face.vertices);
            face.vertices.forEach((i) => {
                const vertexIndex = _verticesMapping[i];
                if (vertexIndex !== undefined) {
                    // Add 1/3 the area of each triangle to neighboring vertices.
                    mass[vertexIndex] += epsilon * area / 3;
                }
            });
        });
        // Use vertex masses to constuct a diagonal vector of length 3*numVertices.
        const massVec = linear_algebra_js_1.DenseMatrix.zeros(numVertices * 3, 1);
        mass.forEach((m, i) => {
            massVec.set(m, 3 * i, 0);
            massVec.set(m, 3 * i + 1, 0);
            massVec.set(m, 3 * i + 2, 0);
        });
        // Create a diagonal matrix from massVec.
        return linear_algebra_js_1.SparseMatrix.diag(massVec);
    }
    /**
     * Returns the G matrix of the mesh.
     * @private
     * @returns {SparseMatrix} A matrix of dimension
     * [numEdges + numCreases, numEdges + numCreases] (excluding fixed/undriven edges/creases)
     * with the geometric stiffness of each constraint along its diagonal.
     */
    _calcGMatrix() {
        // G is a diagonal matrix of stiffnesses.
        // Dimensions: [numEdges + numCreases, numEdges + numCreases]
        // (excluding fixed/undriven edges/creases)
        const { _triangleMesh, _edgesMapping, _creasesMapping } = this;
        // Get the geometric stiffnesses of each constraint.
        const edgesK = this._calcEdgesK();
        const creasesK = this._calcCreasesK();
        // First construct a vector of terms along the diagonal.
        const diag = [];
        // Fill with axial stiffnesses.
        _triangleMesh.edges.forEach((edge, i) => {
            // Don't add fixed edges.
            if (_edgesMapping[i] === undefined) {
                return;
            }
            diag.push(edgesK[i]);
        });
        // Fill with crease stiffnesses.
        _triangleMesh.edges.forEach((edge, i) => {
            // Don't add fixed/undriven creases.
            if (_creasesMapping[i] === undefined) {
                return;
            }
            diag.push(creasesK[i]);
        });
        const diagMat = linear_algebra_js_1.DenseMatrix.zeros(diag.length, 1);
        diag.forEach((entry, i) => diagMat.set(entry, i, 0));
        // Use diagonal vector to construct a diagonal matrix.
        return linear_algebra_js_1.SparseMatrix.diag(diagMat);
    }
    /**
     * Returns the compatibility matrix C of the mesh.
     * @private
     * @returns {SparseMatrix} A matrix of dimension
     * [numEdges + numCreases, numVertices * 3] (excluding fixed/undriven edges/creases/vertices)
     * that relates change in vertex positions in 3 space to changes in constraint space
     * (eg edge length or dihedral angle).
     */
    _calcCMatrix() {
        const { _triangleMesh, _verticesMapping, _edgesMapping, _creasesMapping } = this;
        const numVertices = this._countDefined(_verticesMapping);
        const numEdges = this._countDefined(_edgesMapping);
        const numCreases = this._countDefined(_creasesMapping);
        // Construct a list of triplets (i, j, val) that define all the non-zero
        // elements of the C matrix.
        const Ctriplets = new linear_algebra_js_1.Triplet(numEdges + numCreases, 3 * numVertices);
        _triangleMesh.edges.forEach((edge, i) => {
            const { edgeVertex1, edgeVertex2, leftVertex, rightVertex, } = edge;
            const edgeIndex = _edgesMapping[i];
            if (edgeIndex !== undefined) {
                // Add entries for the jacobian of each axial constraint.
                const coefEdge = this._jacobCoefAxial(edge);
                const index1 = _verticesMapping[edgeVertex1];
                if (index1 !== undefined) { // Check that vertex is not fixed.
                    coefEdge[0].forEach((val, j) => {
                        Ctriplets.addEntry(val, edgeIndex, 3 * index1 + j);
                    });
                }
                const index2 = _verticesMapping[edgeVertex2];
                if (index2 !== undefined) { // Check that vertex is not fixed.
                    coefEdge[1].forEach((val, j) => {
                        Ctriplets.addEntry(val, edgeIndex, 3 * index2 + j);
                    });
                }
            }
            const creaseIndex = _creasesMapping[i];
            if (creaseIndex !== undefined) {
                // Add entries for the jacobian of each crease constraint.
                const coefCrease = this._jacobCoefCrease(edge);
                const index1 = _verticesMapping[leftVertex];
                const index2 = _verticesMapping[rightVertex];
                const index3 = _verticesMapping[edgeVertex1];
                const index4 = _verticesMapping[edgeVertex2];
                if (index1 !== undefined) { // Check that vertex is not fixed.
                    coefCrease[0].forEach((val, j) => {
                        Ctriplets.addEntry(val, creaseIndex, 3 * index1 + j);
                    });
                }
                if (index2 !== undefined) { // Check that vertex is not fixed.
                    coefCrease[1].forEach((val, j) => {
                        Ctriplets.addEntry(val, creaseIndex, 3 * index2 + j);
                    });
                }
                if (index3 !== undefined) { // Check that vertex is not fixed.
                    coefCrease[2].forEach((val, j) => {
                        Ctriplets.addEntry(val, creaseIndex, 3 * index3 + j);
                    });
                }
                if (index4 !== undefined) { // Check that vertex is not fixed.
                    coefCrease[3].forEach((val, j) => {
                        Ctriplets.addEntry(val, creaseIndex, 3 * index4 + j);
                    });
                }
            }
        });
        // Return sparse matrix from non-zero triplets.
        return linear_algebra_js_1.SparseMatrix.fromTriplet(Ctriplets);
    }
    /**
     * Returns a residual vector for all the constraints in the mesh.
     * @private
     * @returns {DenseMatrix} A vector of dimension [numEdges + numCreases]
     * (excluding fixed/undriven edges/creases) that gives the difference between the
     * current lengths/dihedral angles of the mesh constraints with their target values.
     */
    _calcResidual() {
        const { _triangleMesh, _nominalEdgeLengths, _targetDihedrals, _edgesMapping, _creasesMapping, _dihedrals, } = this;
        const edgeLengths = _triangleMesh.edges.map(edge => this._calcEdgeLength(edge));
        const e = [];
        // First loop through all axial constraints.
        edgeLengths.forEach((length, i) => {
            if (_edgesMapping[i] === undefined) {
                return;
            }
            e.push(_nominalEdgeLengths[i] - length);
        });
        // Then loop through all crease constraints.
        _dihedrals.forEach((angle, i) => {
            if (_creasesMapping[i] === undefined) {
                return;
            }
            const targetDihedral = _targetDihedrals[i];
            if (targetDihedral === undefined || angle === undefined) {
                e.push(0);
            }
            else {
                // Speed limit on speed of folding to prevent instability.
                let angleDiff = targetDihedral - angle;
                const tol = 15 / 180 * Math.PI;
                if (Math.abs(targetDihedral - angle) > tol) {
                    angleDiff = tol * (angleDiff < 0 ? -1 : 1);
                }
                e.push(angleDiff);
            }
        });
        const eMat = linear_algebra_js_1.DenseMatrix.zeros(e.length, 1);
        e.forEach((entry, i) => eMat.set(entry, i, 0));
        return eMat;
    }
    /**
     * Updates the vertex positions (this._positions) according to a
     * displacement vector and scaling factor.
     * @private
     * @param {DenseMatrix} displacementVector A vector of vertex displacements.
     * @param {number} scale Scaling factor for step size.
     */
    _updatePositions(displacementVector, scale) {
        const { _positions, _verticesMapping } = this;
        _verticesMapping.forEach((map, i) => {
            if (map === undefined) {
                // no change in position for fixed vertices
                return;
            }
            const position = _positions[i];
            position[0] += scale * displacementVector.get(3 * map, 0);
            position[1] += scale * displacementVector.get(3 * map + 1, 0);
            position[2] += scale * displacementVector.get(3 * map + 2, 0);
        });
    }
    /**
     * Calculate a scaling factor for displacing mesh vertices along a linear displacement vector.
     * TODO: Currently we are using a constant for this, eventually we could implement
     * an adaptive step size to get faster convergence.
     * @private
     * @returns {number} Scaling factor for displacing mesh vertices.
     */
    /* eslint-disable-next-line class-methods-use-this */
    _calcStepScalingFactor() {
        // const { creasesMapping } = this;
        // // transform the displacement vector into the local coord system of the constraints
        // const displacementsLCS = C.timesDense(displacementVector);
        // // calculat maximum angular displacement
        // let maxAngDisp = 0;
        // edges.forEach((edge, i) => {
        //     const creaseIndex = creasesMapping[i];
        //     if (creaseIndex === undefined) {
        //         return;
        //     }
        //     const angDisp = displacementsLCS.get(creaseIndex, 0);
        //     if (Math.abs(angDisp) > Math.abs(maxAngDisp)) {
        //         maxAngDisp = angDisp;
        //     }
        // });
        return 0.2;
    }
    /**
     * Tests if the solver has converged on a static solution.
     * @private
     * @returns {boolean} Result of convergence test.
     */
    _hasConverged(e) {
        const lengthE = this._countDefined(this._edgesMapping)
            + this._countDefined(this._creasesMapping);
        return e.norm(2) / lengthE < EPS;
    }
    _rigidRotateEdges() {
        const { _forwardGroupLookup, _triangleMesh, _crease, _positions, _dihedrals, _targetDihedrals, } = this;
        // Flag to tell us if we need to calc a new residual at the end of all transforms.
        let changed = false;
        // Try to perform rigid transform at each crease.
        _triangleMesh.edgesForwardMapping.forEach((segments, creaseEdgeIndex) => {
            // Ignore edges with no constraint set.
            const targetDihedral = _targetDihedrals[segments[0]];
            if (targetDihedral === undefined) {
                return;
            }
            // Check if this is a valid edge for a rigid transform.
            const edge = _crease.edges[creaseEdgeIndex];
            if (!analysis_1.edgeCanBeRigidlyTransformed(edge, _forwardGroupLookup)) {
                return;
            }
            const leftGroup = _forwardGroupLookup[edge.leftFace.index];
            const rightGroup = _forwardGroupLookup[edge.rightFace.index];
            // Check if target dihedral is different than current dihedral.
            let weightedSum = 0;
            let totalLength = 0;
            for (let i = 0; i < segments.length; i++) {
                const segmentIndex = segments[i];
                const dihedral = _dihedrals[segmentIndex];
                const length = _triangleMesh.edges[segmentIndex].targetLength;
                weightedSum += dihedral * length;
                totalLength += length;
            }
            const avgDihedral = weightedSum / totalLength;
            const angleDiff = targetDihedral - avgDihedral;
            if (Math.abs(angleDiff) < EPS) {
                return;
            }
            // Find the child group, which should be transformed relative to the other.
            const { subtreeFaces } = analysis_1.getGroupToTransform(leftGroup, rightGroup);
            // Calc transform.
            const v1 = _positions[_triangleMesh.verticesForwardMapping[edge.vertex1.index][0]];
            const v2 = _positions[_triangleMesh.verticesForwardMapping[edge.vertex2.index][0]];
            let rotationAxis = common_1.vec.normalize(common_1.vec.subtract(v2, v1));
            const centerOfRotation = v1;
            if (rightGroup.subtreeFaces === subtreeFaces) {
                // Reverse transform if needed.
                rotationAxis = common_1.vec.multiply(rotationAxis, -1);
            }
            // Transform all vertices in subtree,
            // being careful not to transform the same vertex multiple times.
            const visitedVertices = {};
            const m1 = common_1.mat.matrix4FromTranslation(common_1.vec.multiply(centerOfRotation, -1));
            const m2 = common_1.mat.matrix4FromRotationAxisAngle(rotationAxis, angleDiff);
            const m3 = common_1.mat.matrix4FromTranslation(centerOfRotation);
            const transform = common_1.mat.multiply(common_1.mat.multiply(m3, m2), m1);
            subtreeFaces.forEach(creaseFace => {
                _triangleMesh.facesForwardMapping[creaseFace.index].forEach((triangleIndex) => {
                    const { vertices } = _triangleMesh.faces[triangleIndex];
                    vertices.forEach((vertexIndex) => {
                        if (visitedVertices[vertexIndex]) {
                            return;
                        }
                        let position = _positions[vertexIndex];
                        position = common_1.vec.applyTransform(position, transform);
                        _positions[vertexIndex] = position;
                        visitedVertices[vertexIndex] = true;
                    });
                });
            });
            changed = true;
            // Recompute internal variables (normals and dihedrals).
            subtreeFaces.forEach(creaseFace => {
                _triangleMesh.facesForwardMapping[creaseFace.index].forEach((triangleIndex) => {
                    this._normals[triangleIndex] = this._calcFaceNormal(_triangleMesh.faces[triangleIndex].vertices);
                });
            });
            // Multiple creases may have changed by the previous transform (not just the crease at the current creaseEdgeIndex).
            // Loop through all edges and check if dihedral has changed.
            // In these cases, we can set the current dihedral to the targetDihedral.
            _triangleMesh.edgesForwardMapping.forEach(edges => {
                edges.forEach(edgeIndex => {
                    const dihedral = this._calcEdgeDihedral(_triangleMesh.edges[edgeIndex]);
                    if (dihedral === undefined) {
                        return;
                    }
                    if (this._dihedrals[edgeIndex] !== undefined
                        && Math.abs(this._dihedrals[edgeIndex] - dihedral) > EPS) {
                        this._dihedrals[edgeIndex] = targetDihedral;
                    }
                });
            });
        });
        return changed;
    }
    /**
     * Steps the solver forward by one step.
     * @private
     */
    _step(_e) {
        const lastDihedrals = this._dihedrals;
        // First try to do a rigid transform if possible.
        const changed = this._rigidRotateEdges();
        // If needed, do a solve as well.
        const e = changed ? this._calcResidual() : _e;
        if (changed && this._hasConverged(e)) {
            this._angularVelocities = this._triangleMesh.edges.map(() => 0);
            this._displacementVector = undefined;
        }
        else {
            const G = this._G;
            const M = this._M;
            // Recompute matrices and solve linear system.
            const C = this._calcCMatrix();
            const Ctrans = C.transpose();
            const CtransG = Ctrans.timesSparse(G);
            const K = CtransG.timesSparse(C).plus(M); // K = C^T * G * C + M
            const f = CtransG.timesDense(e); // F = C^T * G * e
            // Solve Kx = f.
            // displacementVector = (K.lu()).solveSquare(f); // this is slightly slower
            const displacementVector = (K.chol()).solvePositiveDefinite(f);
            const stepScale = this._calcStepScalingFactor();
            this._updatePositions(displacementVector, stepScale);
            this._displacementVector = displacementVector;
        }
        // Delete all emscripten mem except this._G, this._M, and this._displacementVector.
        this._dumpMemory();
        // Update internal variables.
        this._normals = this._calcNormals();
        this._dihedrals = this._calcDihedrals();
        this._angularVelocities = this._dihedrals.map((dihedral, index) => {
            const lastDihedral = lastDihedrals[index];
            return dihedral === undefined || lastDihedral === undefined ? undefined
                : dihedral - lastDihedral;
        });
        // Notify all listeners.
        this._listeners.forEach((listener) => {
            listener(this.positions, this.dihedrals, this.normals, this.triangleMesh);
        });
    }
    addEventListener(listener) {
        if (this._listeners.indexOf(listener) >= 0) {
            throw new Error('Listener has already been added.');
        }
        this._listeners.push(listener);
    }
    removeEventListener(listener) {
        this._listeners.splice(this._listeners.indexOf(listener), 1);
    }
    _dumpMemory() {
        // Let the Emscripten memory manager know it can free all heap-allocated matrices
        // except G, M, and displacementVector.
        // This prevents memory usage from growing without bound.
        const arraysToKeep = [];
        if (this._G) {
            arraysToKeep.push(this._G);
        }
        if (this._M) {
            arraysToKeep.push(this._M);
        }
        if (this._displacementVector) {
            arraysToKeep.push(this._displacementVector);
        }
        linear_algebra_js_1.memoryManager.deleteExcept(arraysToKeep);
    }
    reset() {
        // Init with no target dihedrals for now.
        this._targetDihedrals = this._triangleMesh.edges.map(() => undefined);
        // Make a copy of the 3D positions of the vertices of the mesh.
        // this.positions will be updated each time step() is called.
        this._positions = this._triangleMesh.vertices.map((vertex) => [vertex.position2D[0], vertex.position2D[1], 0]);
        // Store the current normals of all the triangular faces.
        // this.normals is updated each time step() is called.
        this._normals = this._calcNormals();
        // Save the last dihedral angles of the active creases, this is used to
        // maintain continuity across 2pi <-> 0 boundary as we calculate new dihedral angles.
        // this._dihedrals is updated each time step() is called.
        this._dihedrals = this._calcDihedrals();
        this._angularVelocities = this._triangleMesh.edges.map(() => undefined);
        // Save the current displacement vector for the free vertices, used to test convergence.
        // Displacement vector may not need to be stored on "this" of convergence test changes.
        // this.displacement vector is updated each time step() is called
        this._displacementVector = undefined;
        this._dumpMemory();
        // Notify all listeners.
        this._listeners.forEach((listener) => {
            listener(this.positions, this.dihedrals, this.normals, this.triangleMesh);
        });
    }
    /**
     * Steps the solver forward by one step, unless it has already reached convergence.
     * @returns {boolean} true if updates made to internal state, else false.
     */
    step() {
        const e = this._calcResidual();
        if (this._hasConverged(e)) {
            this._angularVelocities = this._triangleMesh.edges.map(() => 0);
            this._dumpMemory();
            return false;
        }
        this._step(e);
        return true;
    }
    /**
     * Steps the solver forward until it reaches convergence or times out.
     * @param {number} [maxSteps=1000] The maximum number of iterations to run for before timeout.
     * Default set to 1000.
     * @returns {Error | undefined} If the solver is not able to reach convergence before timing
     * out, it will return an error. Otherwise, it returns undefined.
     */
    solve(maxSteps = 1000) {
        // Drive the system to a final resting state.
        // Most patterns (eg from templatemaker) take about 150 steps
        // to converge to this.displacementVector.norm(2) < 1e-10.
        let numSteps = 0;
        while (numSteps < maxSteps) {
            const e = this._calcResidual();
            if (this._hasConverged(e)) {
                this._dumpMemory();
                break;
            }
            this._step(e);
            numSteps += 1;
        }
        if (numSteps >= maxSteps) {
            return new Error('Hit max iteration num before converging on static solution.');
        }
        this._angularVelocities = this._triangleMesh.edges.map(() => 0);
        return undefined;
    }
    showFlatState() {
        // Notify all listeners.
        this._listeners.forEach((listener) => {
            listener(this.flatPositions, this.flatDihedrals, this.normals, this.triangleMesh);
        });
    }
    unshowFlatState() {
        // Notify all listeners.
        this._listeners.forEach((listener) => {
            listener(this.positions, this.dihedrals, this.normals, this.triangleMesh);
        });
    }
    /**
     * Clears all stored data on the Solver object.
     * @private
     */
    _clear() {
        this._isInitialized = false;
        // Arrays.
        this._listeners = [];
        this._triangleMesh = {
            vertices: [],
            edges: [],
            faces: [],
            verticesForwardMapping: [],
            edgesForwardMapping: [],
            facesForwardMapping: [],
        };
        this._positions = [];
        this._normals = [];
        this._nominalEdgeLengths = [];
        this._targetDihedrals = [];
        this._dihedrals = [];
        this._angularVelocities = [];
        this._verticesMapping = [];
        this._edgesMapping = [];
        this._creasesMapping = [];
        // Matrices allocated by Emscripten.
        this._displacementVector = undefined;
        this._M = undefined;
        this._G = undefined;
        // Let the Emscripten memory manager know it can free all heap-allocated matrices.
        linear_algebra_js_1.memoryManager.deleteExcept([]);
    }
}
exports.Solver = Solver;
//# sourceMappingURL=Solver.js.map